// Web Component: ThemeLoader
// Descrizione: Gestisce la visualizzazione di un loader globale come custom element.
// Attributi accettati:
//   - data-global: se presente, il loader viene gestito come globale e si nasconde/mostra su cambio pagina
// Eventi emessi: nessuno
// Dipendenze: nessuna obbligatoria, ma richiede presenza di Bootstrap per le classi CSS
// Limiti: richiede struttura HTML compatibile
// Utilizzo: inserito nelle pagine dove serve mostrare un loader durante operazioni asincrone
class ThemeLoader extends HTMLElement {
  isGlobal = false;
  static classes = {
    active: 'd-flex',
    hidden: 'd-none',
  }
  
  connectedCallback() {
    this.isGlobal = this.dataset.global !== undefined;

    if(this.isGlobal) {
      window.onpageshow = () => {
        this.hide();
      }
      window.onpagehide = () => {
        this.hide();
      }
    }
  }
  
  show = () => {
    this.classList.remove(ThemeLoader.classes.hidden);
    this.classList.add(ThemeLoader.classes.active);
  }

  hide = () => {
    this.classList.remove(ThemeLoader.classes.active);
    this.classList.add(ThemeLoader.classes.hidden);
  }
}
if (!window.customElements.get("theme-loader")) {
  window.customElements.define("theme-loader", ThemeLoader);
}

// Web Component: ReadMore
// Descrizione: Gestisce la funzionalità "leggi di più" per testi lunghi, permettendo l'espansione/collasso del contenuto.
// Attributi accettati:
//   - data-more-text: testo del link per espandere il contenuto
// Eventi emessi: nessuno
// Dipendenze: nessuna obbligatoria
// Limiti: richiede che il contenuto sia in un div figlio diretto
// Utilizzo: inserito dove serve mostrare solo una parte del testo con possibilità di espansione
class ReadMore extends HTMLElement {
  expanded; expandible; link; text;
  static classes = {
    expanded: 'expanded',
    link: 'fw-bold d-block mt-2 text-decoration-underline'
  }

  constructor(){
    super();
    this.init();
  }

  init = () => {
    this.text = this.querySelector(':scope > div');
    this.expanded = false;
    this.expandible = this.text.scrollHeight > this.text.clientHeight;
    if(this.expandible){
      this.addLink();
    }
  }

  addLink = () => {
    this.link = document.createElement('a');
    this.link.setAttribute('href', '#');
    this.link.innerText = this.dataset.moreText;
    ReadMore.classes.link.split(' ').forEach(c => {
      this.link.classList.add(c);
    });
    this.link.addEventListener('click', (e) => {
      this.toggle(e);
    });
    this.appendChild(this.link);
  }

  toggle = (e) => {
    if(e != undefined)
      e.preventDefault();
    if(this.expanded)
      this.close();
    else
      this.open();
  }

  open = () => {
    this.classList.add(ReadMore.classes.expanded);
    this.link.innerText = this.dataset.lessText;
    this.expanded = true;
  }

  close = () => {
    this.classList.remove(ReadMore.classes.expanded);
    this.link.innerText = this.dataset.moreText;
    this.expanded = false;
  }
}
if (!window.customElements.get("read-more")) {
  window.customElements.define("read-more", ReadMore);
}

// Web Component: ThemeHeader
// Descrizione: Gestisce l’header principale del sito, con supporto per trasparenza, megamenu e classi dinamiche.
// Attributi accettati:
//   - data-transparent: classi CSS da applicare quando l’header è trasparente
//   - data-normal: classi CSS da applicare quando l’header è normale
// Eventi emessi:
//   - Nessuno custom, ma ascolta Utils.E_MEGAMENU_SHOW/HIDE
// Dipendenze: presenza di theme-megamenu, Utils
// Limiti: richiede struttura HTML compatibile e presenza di megamenu
// Utilizzo: inserito come header principale nelle pagine
class ThemeHeader extends HTMLElement {
  transparentClass; normalClass; canTransparent;
  static offset = 100;
  static els = {
    megamenu: 'theme-megamenu'
  }

  constructor() {
    super();
    this.transparentClass = this.dataset.transparent != undefined ? this.dataset.transparent.split(' ') : [];
    this.normalClass = this.dataset.normal != undefined ? this.dataset.normal.split(' ') : [];
    this.megamenus = this.querySelectorAll(ThemeHeader.els.megamenu);
    this.canTransparent = this.transparentClass.length > 0;
    this.init();
  }

  init = () => {
    if(this.transparentClass.length > 0){
      this.checkBg();
      window.addEventListener('scroll', () => this.checkBg());
      this.addEventListener('mouseover', () => this.unsetTransparent());
      this.megamenus.forEach(el => { 
        el.addEventListener(Utils.E_MEGAMENU_SHOW, () => {
          this.canTransparent = false;
          this.unsetTransparent();
        });
        el.addEventListener(Utils.E_MEGAMENU_HIDE, () => {
          this.canTransparent = !document.querySelectorAll('theme-megamenu.show').length;
          this.checkBg();
        });
      });
    }
    this.addEventListener('mouseleave', () => {
      if(this.transparentClass.length > 0){
        this.checkBg();
      }
      this.closeMegamenu();
    });
  }

  checkBg = () => {
    if(window.scrollY < ThemeHeader.offset) {
      this.setTransparent();
    } else {
      this.unsetTransparent();
    }
  }

  closeMegamenu = () => {
    this.megamenus.forEach(el => {
      el.hide();
    });
  } 

  setTransparent = () => {
    if(this.canTransparent){
      this.transparentClass.forEach(className => {
        this.classList.add(className);
      });
      this.normalClass.forEach(className => {
        this.classList.remove(className);
      });
    }
  }

  unsetTransparent = () => {
    this.transparentClass.forEach(className => {
      this.classList.remove(className);
    });
    this.normalClass.forEach(className => {
      this.classList.add(className);
    });
  }
}
if (!window.customElements.get("theme-header")) {
  window.customElements.define("theme-header", ThemeHeader);
}

// Web Component: MobilePanel
// Descrizione: Gestisce la visualizzazione di pannelli mobili per la navigazione su dispositivi mobile.
// Attributi accettati: nessuno specifico
// Eventi emessi: nessuno
// Dipendenze: presenza di #main-mobile
// Limiti: richiede struttura HTML compatibile
// Utilizzo: usato per mostrare pannelli laterali su mobile
class MobilePanel extends HTMLElement {
  static classes = {
    show: ['active']
  }

  connectedCallback() {
    this.main = document.querySelector('#main-mobile');
  }

  show = () => {
    MobilePanel.hideAll();
    this.main.classList.add('d-none');
    MobilePanel.classes.show.forEach(className => {
      this.classList.add(className);
    });
  }

  hide = () => {
    this.main.classList.remove('d-none');
    MobilePanel.classes.show.forEach(className => {
      this.classList.remove(className);
    });
  }

  static hideAll = () => {
    document.querySelectorAll('mobile-panel').forEach(el => {
      el.hide();
    });
  }
}
if (!window.customElements.get("mobile-panel")) {
  window.customElements.define("mobile-panel", MobilePanel);
}

// Web Component: MobileBtn
// Descrizione: Gestisce i pulsanti che aprono/chiudono i MobilePanel tramite data-target/data-close.
// Attributi accettati:
//   - data-target: selettore del pannello da mostrare
//   - data-close: se presente, chiude tutti i pannelli
// Eventi emessi: nessuno
// Dipendenze: MobilePanel
// Limiti: richiede struttura HTML compatibile e presenza di MobilePanel
// Utilizzo: usato per attivare/chiudere pannelli mobili
class MobileBtn extends HTMLElement {
  panel;

  constructor() {
    super();
    this.panel = document.querySelector(this.dataset.target);
    this.init();
  }

  init = () => {
    if(this.panel != undefined) {
      this.addEventListener('click', () => this.panel.show());
    }
    if(this.dataset.close != undefined){
      this.addEventListener('click', () => MobilePanel.hideAll());
    }
  }
}
if (!window.customElements.get("mobile-btn")) {
  window.customElements.define("mobile-btn", MobileBtn);
}

// Web Component: ScrollingText
// Descrizione: Gestisce un testo scorrevole (marquee) duplicando i figli per l’effetto infinito.
// Attributi accettati: nessuno specifico
// Eventi emessi: nessuno
// Dipendenze: nessuna
// Limiti: richiede che i figli siano elementi da scorrere
// Utilizzo: usato per banner/testi scorrevoli
class ScrollingText extends HTMLElement {
  displayed;

  connectedCallback() {
    this.displayed = getComputedStyle(document.documentElement).getPropertyValue("--marquee-elements-displayed");
    this.init();
  }

  init = () => {
    this.style.setProperty("--marquee-elements", this.children.length);
    for(var i=0; i<this.displayed; i++){
      this.appendChild(this.children[i].cloneNode(true));
    }
  }
}
if (!window.customElements.get("scrolling-text")) {
  window.customElements.define("scrolling-text", ScrollingText);
}

// Web Component: ThemeMegamenu
// Descrizione: Gestisce il megamenu principale, con supporto per click, eventi custom e chiusura automatica.
// Attributi accettati:
//   - data-level: livello del menu
//   - data-parents: lista dei parent
//   - data-submenu: presenza indica sottomenu
// Eventi emessi:
//   - Utils.E_MEGAMENU_SHOW: quando il megamenu viene mostrato
//   - Utils.E_MEGAMENU_HIDE: quando il megamenu viene nascosto
// Dipendenze: Utils, Shopify.analytics
// Limiti: richiede struttura HTML compatibile e presenza di dati corretti
// Utilizzo: usato come menu principale desktop
class ThemeMegamenu extends HTMLElement {

  connectedCallback() {
    this.clickoutEvent = this.clickout.bind(this);
    this.querySelectorAll("[data-level]").forEach((el) => {
      el.addEventListener('click', (e) => this.menuClick(el, e));
    });
  }

  menuClick = (el, e) => {
    e.preventDefault();
    const isSubmenu = el.dataset.submenu !== undefined;
    const data = {label: el.innerText, level: el.dataset.level, parents: el.dataset.parents.split(','), isSubmenu: isSubmenu};
    Shopify.analytics.publish(Utils.E_MENU_CLICK, data);
    window.dispatchEvent(new CustomEvent(Utils.E_MENU_CLICK, { detail: data }));
    if(e.defaultPrevented){
      window.location.href = el.href;
    }
  }

  clickout = (e) => {
    if (!this.contains(e.target) && this.classList.contains('show') && !e.target.closest('megamenu-link')) {
      this.hide();
    }
  }

  show = () => {
    document.addEventListener('click', this.clickoutEvent, false);
    document.querySelectorAll('theme-megamenu').forEach(el => {
      el.hide();
    });
    this.classList.add('show');
    this.dispatchEvent(new CustomEvent(Utils.E_MEGAMENU_SHOW));
  }

  hide = () => {
    if(this.classList.contains('show')){
      this.classList.remove('show');
      this.dispatchEvent(new CustomEvent(Utils.E_MEGAMENU_HIDE));
      document.removeEventListener('click', this.clickoutEvent, false);
    }
  }
}
if (!window.customElements.get("theme-megamenu")) {
  window.customElements.define("theme-megamenu", ThemeMegamenu);
}

// Web Component: MegamenuLink
// Descrizione: Gestisce i link che attivano il megamenu, con supporto per hover e dataset personalizzato.
// Attributi accettati:
//   - data-megamenu: selettore del megamenu da attivare
// Eventi emessi: nessuno
// Dipendenze: ThemeMegamenu
// Limiti: richiede struttura HTML compatibile e presenza di megamenu
// Utilizzo: usato per attivare il megamenu da link
class MegamenuLink extends HTMLElement {
  megamenu;

  connectedCallback() {
    this.megamenu = document.querySelector(this.dataset.megamenu);
    this.init();
  }

  init = () => {
    if(this.megamenu != undefined){
      this.addEventListener('mouseover', () => this.show());
      this.addEventListener('click', () => this.clickmenu());

      this.megamenu.addEventListener(Utils.E_MEGAMENU_SHOW, () => {
        this.classList.add('active');
      });
      this.megamenu.addEventListener(Utils.E_MEGAMENU_HIDE, () => {
        this.classList.remove('active');
      });
    }
  }

  clickmenu = () => {
    const data = {label: this.innerText, level: 1};
    Shopify.analytics.publish(Utils.E_MENU_CLICK, data);
    window.dispatchEvent(new CustomEvent(Utils.E_MENU_CLICK, { detail: data }));
  }

  show = () => {
    this.megamenu.show();
  }

}
if (!window.customElements.get("megamenu-link")) {
  window.customElements.define("megamenu-link", MegamenuLink);
}

// Web Component: VideoControls
// Descrizione: Gestisce i controlli di riproduzione video e overlay custom.
// Attributi accettati: nessuno specifico
// Eventi emessi: nessuno
// Dipendenze: presenza di un elemento video e overlay con data-overlay
// Limiti: richiede struttura HTML compatibile
// Utilizzo: usato per controllare la riproduzione di video custom
class VideoControls extends HTMLElement {
  video; overlay;

  connectedCallback() {
    this.video = this.querySelector('video');
    this.overlay = this.querySelector('[data-overlay]');
    this.init();
  }

  init = () => {
    if(this.overlay != undefined){
      this.overlay.addEventListener('click', () => this.toggle());
    }
    this.video.addEventListener('play', () => this.played());
    this.video.addEventListener('pause', () => this.paused());
  }

  play = () => {
    this.video.play();
  }

  pause = () => {
    this.video.pause();
  }

  toggle = () => {
    if(this.video.paused)
      this.play();
    else
      this.pause();
  }

  played = () => {
    if(this.overlay != undefined){
      this.overlay.classList.add('d-none');
    }
  }

  paused = () => {
    if(this.overlay != undefined){
      this.overlay.classList.remove('d-none');
    }
  }
}
if (!window.customElements.get("video-controls")) {
  window.customElements.define("video-controls", VideoControls);
}

// Web Component: ProductList
// Descrizione: Gestisce la visualizzazione di una lista prodotti e il tracciamento degli eventi di visualizzazione/click.
// Attributi accettati: nessuno specifico
// Eventi emessi:
//   - Utils.E_LIST_VIEWED: quando la lista entra in viewport
//   - Utils.E_CAROUSEL_CLICK: quando viene cliccato un prodotto
// Dipendenze: presenza di script[data-describe="product-list"], Shopify.analytics
// Limiti: richiede struttura HTML compatibile e dati JSON corretti
// Utilizzo: usato per caricare e tracciare liste prodotti
class ProductList extends HTMLElement {
  data;

  connectedCallback() {
    this.data = JSON.parse(this.querySelector('script[data-describe="product-list"]').innerText);
    console.log(this.data);
    this.init();
  }

  init = () => {
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if(entry.isIntersecting){
          window.dispatchEvent(new CustomEvent(Utils.E_LIST_VIEWED, { detail: this.data }));
          for(let i = 0; i < this.data.products.length; i += 5){
            let chunk = this.data.products.slice(i, i + 5);
            let chunkData = {...this.data };
            chunkData.offset = i;
            chunkData.products = chunk;
             Shopify.analytics.publish(Utils.E_LIST_VIEWED, chunkData);
          }
          //Shopify.analytics.publish(Utils.E_LIST_VIEWED, this.data);
          observer.unobserve(entry.target);
        }
      });
    });
    observer.observe(this);
    this.querySelectorAll('product-card').forEach((el) => {
      el.addEventListener(Utils.E_PRODUCT_CLICKED, (e) => {
        const data = {list: this.data, product: e.detail};
        Shopify.analytics.publish(Utils.E_CAROUSEL_CLICK, data);
        window.dispatchEvent(new CustomEvent(Utils.E_CAROUSEL_CLICK, {detail: data}));
      });
    });
  }
}
if (!window.customElements.get("product-list")) {
  window.customElements.define("product-list", ProductList);
}

// Web Component: SocialIcons
// Descrizione: Gestisce i click sulle icone social e invia eventi di analytics.
// Attributi accettati: nessuno specifico
// Eventi emessi: Utils.E_SOCIAL_CLICK
// Dipendenze: Shopify.analytics
// Limiti: richiede struttura HTML compatibile
// Utilizzo: usato per tracciare click sulle icone social
class SocialIcons extends HTMLElement {

  connectedCallback() {
    this.querySelectorAll('a').forEach((el) => {
      el.addEventListener('click', () => Shopify.analytics.publish(Utils.E_SOCIAL_CLICK, {label: el.getAttribute('aria-label')}));
    });
  }
}
if (!window.customElements.get("social-icons")) {
  window.customElements.define("social-icons", SocialIcons);
}

// Web Component: DynamicSection
// Descrizione: Gestisce sezioni dinamiche con tracciamento view/click banner e CTA.
// Attributi accettati:
//   - data-name, data-page-type, data-page-name
// Eventi emessi:
//   - Utils.E_BANNER_VIEW: quando la sezione entra in viewport
//   - Utils.E_BANNER_CLICK: quando viene cliccato un link/cta
// Dipendenze: Shopify.analytics
// Limiti: richiede struttura HTML compatibile e dati corretti
// Utilizzo: usato per banner dinamici e sezioni promozionali
class DynamicSection extends HTMLElement {
  links; data;

  connectedCallback() {
    this.links = this.querySelectorAll('a,button');
    this.data = {
      label: this.dataset.name,
      pageType: this.dataset.pageType,
      pageName: this.dataset.pageName
    }
    this.init();
  }

  init = () => {
    this.setLinks();
  }

  setObserver = () => {
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if(entry.isIntersecting){
          Shopify.analytics.publish(Utils.E_BANNER_VIEW, this.data);
          window.dispatchEvent(new CustomEvent(Utils.E_BANNER_VIEW, { detail: this.data }));
          this.dispatchEvent(new CustomEvent(Utils.E_BANNER_VIEW, { detail: this.data }));
          observer.unobserve(entry.target);
        }
      });
    });
    observer.observe(this);
  }

  setLinks = () => {
    this.links.forEach((el) => {
      el.addEventListener('click', () => {
        const linkData = this.data;
        linkData.cta = el.getAttribute('aria-label') || el.innerText;
        linkData.url = el.href;
        Shopify.analytics.publish(Utils.E_BANNER_CLICK, linkData);
        window.dispatchEvent(new CustomEvent(Utils.E_BANNER_CLICK, { detail: linkData }));
      });
    });
  }
}
if (!window.customElements.get("dynamic-section")) {
  window.customElements.define("dynamic-section", DynamicSection);
}

// Web Component: AnnouncementBar
// Descrizione: Gestisce la barra annunci e il tracciamento dei click sui link.
// Attributi accettati: nessuno specifico
// Eventi emessi: Utils.E_ANNOUNCEMENT_CLICK
// Dipendenze: Shopify.analytics
// Limiti: richiede struttura HTML compatibile
// Utilizzo: usato per mostrare annunci e banner in alto
class AnnouncementBar extends HTMLElement {
  links;

  connectedCallback() {
    this.links = this.querySelectorAll('[data-link]');
    this.init();
  }

  init = () => {
    this.links.forEach((el) => {
      el.addEventListener('click', () => {
        const data = {
          label: el.dataset.link
        };
        Shopify.analytics.publish(Utils.E_ANNOUNCEMENT_CLICK, data);
        window.dispatchEvent(new CustomEvent(Utils.E_ANNOUNCEMENT_CLICK, { detail: data }));
      });
    });
  }
}
if (!window.customElements.get("announcement-bar")) {
  window.customElements.define("announcement-bar", AnnouncementBar);
}

// Web Component: FooterMain
// Descrizione: Gestisce il footer principale e il tracciamento dei click sui link.
// Attributi accettati:
//   - data-page: nome della pagina corrente
// Eventi emessi: Utils.E_FOOTER_CLICK
// Dipendenze: Shopify.analytics
// Limiti: richiede struttura HTML compatibile
// Utilizzo: usato come footer principale del sito
class FooterMain extends HTMLElement {
  links;

  connectedCallback() {
    this.links = this.querySelectorAll('a');
    this.init();
  }

  init = () => {
    this.links.forEach((el) => {
      el.addEventListener('click', () => {
        const data = {
          label: el.innerText,
          page: this.dataset.page
        };
        if(el.dataset.menu){
          data.menu = el.dataset.menu;
        }
        Shopify.analytics.publish(Utils.E_FOOTER_CLICK, data);
        window.dispatchEvent(new CustomEvent(Utils.E_FOOTER_CLICK, { detail: data }));
      });
    });
  }
}
if (!window.customElements.get("footer-main")) {
  window.customElements.define("footer-main", FooterMain);
}

/* al click su questo componente copia il testo in data-text o il contenuto. se gli si passa un toast di bootstrap lo mostra (tipo "codice copiato!") */
// Web Component: CopyClick
// Descrizione: Permette di copiare testo negli appunti e mostrare feedback tramite toast/popover.
// Attributi accettati:
//   - data-text: testo da copiare
//   - data-toast: selettore toast Bootstrap
//   - data-popover: selettore popover Bootstrap
// Eventi emessi: nessuno
// Dipendenze: Bootstrap (opzionale per toast/popover)
// Limiti: richiede struttura HTML compatibile
// Utilizzo: usato per copiare codici, coupon, ecc.
class CopyClick extends HTMLElement {

  connectedCallback() {
    this.addEventListener('click', () => this.copy());
  }

  copy = () => {
    navigator.clipboard.writeText(this.dataset.text || this.textContent);
    this.feedback();
  }

  feedback = () => {
    if(this.dataset.toast){
      this.toast();
    } else if (this.dataset.popover){
      this.popover();
    }
  }

  popover = () => {
    let popover = this.dataset.popover ? document.querySelector(this.dataset.popover) : null;
    if(popover){
      popover = new bootstrap.Popover(popover);
      popover.show();
      setTimeout(() => {
        popover.dispose();
      }, 2000);
    }
  }

  toast = () => {
    let toast = this.dataset.toast ? document.querySelector(this.dataset.toast) : null;
    if(toast){
      toast = bootstrap.Toast.getOrCreateInstance(toast);
      toast.show();
    }
  }

}
if (!window.customElements.get("copy-click")) {
  window.customElements.define("copy-click", CopyClick);
}

// Web Component: ReelSwiper
// Descrizione: Estende ThemeSwiper per caroselli di reel/video, gestendo eventi di cambio slide e attivazione.
// Attributi accettati:
//   - data-reel-autoplay: se assente, attiva observer per eventi banner
// Eventi emessi: nessuno custom, ma attiva metodi su slide
// Dipendenze: ThemeSwiper, Swiper.js
// Limiti: richiede struttura HTML compatibile e slide custom
// Utilizzo: usato per caroselli di reel/video
class ReelSwiper extends ThemeSwiper {
  static SLIDECHANGE = 'reels:change';

  afterInit = () => {
    this.swiper.on('slideChangeTransitionEnd', this.changeEvent.bind(this));
    this.swiper.on('afterInit', this.changeEvent.bind(this));

    const section = this.closest('dynamic-section');
    if(section && this.dataset.reelAutoplay == undefined){
      section.setObserver();
      section.addEventListener(Utils.E_BANNER_VIEW, this.changeEvent.bind(this));
    }
  }

  changeEvent = (e) => {
    const activeSlide = this.querySelector('reel-slide.swiper-slide-active');
    this.querySelectorAll('reel-slide').forEach((el) => {
      el.deactivate();
    });
    if(activeSlide != undefined){
      activeSlide.activate();
    }
  }
}
if (!window.customElements.get("reel-swiper")) {
  window.customElements.define("reel-swiper", ReelSwiper);
}

// Web Component: ReelSlide
// Descrizione: Gestisce una singola slide di reel/video, con supporto per video, iframe Vimeo e audio.
// Attributi accettati: nessuno specifico
// Eventi emessi: nessuno
// Dipendenze: Vimeo.Player (se iframe), presenza di video/audio
// Limiti: richiede struttura HTML compatibile
// Utilizzo: usato come slide nei caroselli reel
class ReelSlide extends HTMLElement {
  video; audio; iframe; player;

  connectedCallback()  {
    this.video = this.querySelector('video');
    this.iframe = this.querySelector('iframe');
    this.getPlayer();
    this.audio = this.querySelector('[data-audio]');

    this.video?.addEventListener('volumechange', this.volume.bind(this));
    this.player?.on('volumechange', this.volume.bind(this));
    this.audio.addEventListener('click', this.toggleMuted.bind(this));
    this.volume();
  }

  toggleMuted = () => {
    if(this.video != undefined){
      this.video.muted = !this.video.muted;
    }
    if(this.iframe != undefined){
      this.player.getVolume().then((volume) => {
        this.player.setVolume(volume == 0 ? 1 : 0);
      });
    }
  }

  volume = (e) => {
    if(this.video != undefined){
      if(this.video.muted){
        this.classList.add('muted');
      } else {
        this.classList.remove('muted');
      }
    }
    if(this.iframe != undefined){
      this.player.getVolume().then((volume) => {
        if(volume == 0){
          this.classList.add('muted');
        } else {
          this.classList.remove('muted');
        }
      });
    }
  }

  activate = () => {
    if(this.video){
      this.video.play();
    }
    if(this.iframe){
      this.player.play();
    }
    this.classList.add('active');
  }

  deactivate = () => {
    if(this.video){
      this.video.pause();
    }
    if(this.iframe){
      this.player.pause();
    }
    this.classList.remove('active');
  }

  getPlayer = () => {
    if(!this.player && this.iframe){
      this.player = new Vimeo.Player(this.iframe);
    }
    return this.player;
  }
}
if (!window.customElements.get("reel-slide")) {
  window.customElements.define("reel-slide", ReelSlide);
}

// Web Component: LazySection
// Descrizione: Carica dinamicamente una sezione HTML quando entra in viewport tramite AJAX.
// Attributi accettati:
//   - data-page: URL della pagina da caricare
//   - data-id: id della sezione da estrarre
// Eventi emessi: nessuno
// Dipendenze: axios
// Limiti: richiede struttura HTML compatibile e dati corretti
// Utilizzo: usato per lazy load di sezioni
class LazySection extends HTMLElement {
  connectedCallback() {
    if(!this.dataset.page || !this.dataset.id){
      console.error('LazySection: manca data-page o data-id');
      return;
    }
    this.setObserver();
  }

  setObserver = () => {
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if(entry.isIntersecting){
          this.load();
          observer.unobserve(entry.target);
        }
      });
    });
    observer.observe(this);
  }

  load = () => {
    axios({
      method: 'GET',
      url: this.dataset.page,
    }).then((response) => {
      this.render(response.data);
    }).catch((error) => {
      console.error('LazySection: errore nel caricamento della sezione', error);
    });
  }

  render = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const section = doc.querySelector(`#${this.dataset.id}`);
    if(!section){
      console.error('LazySection: non ho trovato la sezione', this.dataset.id, 'in', html);
      return;
    }
    this.innerHTML = section.innerHTML;
  }
}
if (!window.customElements.get("lazy-section")) {
  window.customElements.define("lazy-section", LazySection);
}

// Web Component: SwiperContinue
// Descrizione: Gestisce uno slider Swiper con autoplay continuo e loop, configurabile tramite attributi.
// Attributi accettati:
//   - data-space-between: spazio tra slide
//   - data-speed: velocità autoplay
// Eventi emessi: nessuno
// Dipendenze: Swiper.js
// Limiti: richiede struttura HTML compatibile
// Utilizzo: usato per slider continui (es. loghi, prodotti)
class SwiperContinue extends HTMLElement {

  connectedCallback() {
    this.swiper = new Swiper(this, {
      loop: true,
      slidesPerView: "auto",
      freeMode: true,
      loopAddBlankSlides: true,
      loopAdditionalSlides: 10,
      spaceBetween: this.dataset.spaceBetween || 24,
      autoplay: {
        delay: 0,
      },
      speed: parseInt(this.dataset.speed) || 5000,
    });
  }
}
if (!window.customElements.get("swiper-continue")) {
  window.customElements.define("swiper-continue", SwiperContinue);
}

// Web Component: HomeCta
// Descrizione: Gestisce i click sulle call-to-action della home e invia eventi di analytics.
// Attributi accettati:
//   - data-title, data-action, data-type, data-image-type
// Eventi emessi: home_cta:click (Shopify.analytics)
// Dipendenze: Shopify.analytics
// Limiti: richiede struttura HTML compatibile
// Utilizzo: usato per tracciare CTA nella home
class HomeCta extends HTMLElement {
  
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('click', (event) => {
      event.stopPropagation();
      event.preventDefault();
      const data = {
        title: this.dataset.title,
        action: this.dataset.action,
        type: this.dataset.type,
        imageType: this.dataset.imageType,
      };
      Shopify.analytics.publish('home_cta:click', data);
      if(event.defaultPrevented)
        window.location.href = this.querySelector('a').href;
    });
  }
}
if(!window.customElements.get("home-cta")) {
  window.customElements.define("home-cta", HomeCta);
}

class FooterTextSlider extends HTMLElement {
    constructor() {
        super();
    }
    connectedCallback() {
        if(window.sessionStorage.getItem('footerTextHidden') === 'true'){
            this.classList.add('d-none');
            return;
        }
        this.querySelector('[data-hide]').addEventListener('click', () => {
            this.hide();
        });
    }
    hide = () => {
        this.classList.add('d-none');
        window.sessionStorage.setItem('footerTextHidden', 'true');
    }
}
if(!window.customElements.get("footer-text-slider")) {
    window.customElements.define("footer-text-slider", FooterTextSlider);
}